import React, { useState, useEffect, useRef } from 'react';
import { API_BASE_URL, authHeaders } from '../../config';
import {
    IonButton, IonCol, IonContent, IonGrid,
    IonInput, IonItem, IonPage, IonRow,
    IonCard, IonCardContent, IonIcon, IonSpinner, IonSelect, IonSelectOption
} from "@ionic/react";
import { chevronDownOutline, closeCircleOutline, alertCircleOutline } from 'ionicons/icons';
import { Link } from 'react-router-dom';
import { handleRegister } from "./SendForm";
import { PageHeader, AppBackground } from '../../components/PageHeader';
import './form.css';
import { AlertAceptar } from '../../components/alerts/AlertAceptar';

// Búsqueda de posibles duplicados: se consulta cuando el nombre tiene al menos
// NOMBRE_MIN caracteres (o el alias ALIAS_MIN) y el usuario deja de escribir.
const NOMBRE_MIN = 3;
const ALIAS_MIN = 2;
const COINCIDENCIAS_DEBOUNCE_MS = 600;

// ── Tipos ────────────────────────────────────────────────────────────────────

interface Departamento {
    id: number;
    nombre: string;
}

interface Municipio {
    id: number;
    nombre: string;
    departamento_id: number;
}

interface PresentadorOption {
    id: number;
    nombre_completo: string;
    alias?: string;
    numero_cedula?: string;
}

interface Coincidencia {
    id: number;
    nombre_completo: string;
    alias: string;
    numero_cedula: string;
    municipio_nombre: string;
    registrado_por: string;
}

interface FormDataType {
    nombre_completo: string;
    alias: string;
    telefono: string;
    genero: string;
    lugarVotacion: string;
    mesaVotacion: string;
    municipio_id: number | null;
    direccion: string;
    cedula: string;
    referido_id: number | null;  // Relación jerárquica
    // usuario_id lo maneja el backend desde el JWT
}

// ── Componente Selector de Presentador ──────────────────────────────────────

interface PresentadorSelectProps {
    value: PresentadorOption | null;
    onChange: (option: PresentadorOption | null) => void;
    options: PresentadorOption[];
    loading?: boolean;
}

const PresentadorSelect: React.FC<PresentadorSelectProps> = ({
    value, onChange, options, loading
}) => {
    const [inputText, setInputText] = useState(value ? value.nombre_completo : '');
    const [open, setOpen] = useState(false);
    const [filtered, setFiltered] = useState<PresentadorOption[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (value && value.nombre_completo) {
            setInputText(value.nombre_completo);
        } else {
            setInputText('');
        }
    }, [value]);

    useEffect(() => {
        const q = inputText.trim().toLowerCase();
        if (q === '') {
            setFiltered(options);
        } else {
            setFiltered(options.filter(o =>
                o.nombre_completo.toLowerCase().includes(q) ||
                (o.alias && o.alias.toLowerCase().includes(q)) ||
                (o.numero_cedula && o.numero_cedula.includes(q))
            ));
        }
    }, [inputText, options]);

    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
                setInputText(value && value.nombre_completo ? value.nombre_completo : '');
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [value]);

    const handleSelect = (opt: PresentadorOption) => {
        setInputText(opt.nombre_completo);
        onChange(opt);
        setOpen(false);
    };

    const handleClear = () => {
        setInputText('');
        onChange(null);
        setOpen(false);
    };

    return (
        <div className="creatable-select-wrapper" ref={containerRef}>
            <label className="creatable-select-label">Referido por </label>
            <div className={`creatable-select-control ${open ? 'open' : ''}`}>
                <input
                    type="text"
                    className="creatable-select-input"
                    value={inputText}
                    placeholder="Buscar por nombre, alias o cédula..."
                    onFocus={() => setOpen(true)}
                    onChange={e => {
                        setInputText(e.target.value);
                        setOpen(true);
                    }}
                />
                <div className="creatable-select-icons">
                    {loading && <IonSpinner name="crescent" className="select-spinner" />}
                    {inputText && !loading && (
                        <button
                            type="button"
                            className="creatable-clear-btn"
                            onClick={handleClear}
                            tabIndex={-1}
                        >
                            <IonIcon icon={closeCircleOutline} />
                        </button>
                    )}
                    {!loading && (
                        <IonIcon
                            icon={chevronDownOutline}
                            className={`creatable-chevron ${open ? 'rotated' : ''}`}
                            onClick={() => setOpen(v => !v)}
                        />
                    )}
                </div>
            </div>

            {open && (
                <div className="creatable-select-dropdown">
                    <div
                        className={`creatable-option ${!value ? 'selected' : ''}`}
                        onMouseDown={() => { onChange(null); setInputText(''); setOpen(false); }}
                    >
                        <em style={{ opacity: 0.6 }}>Sin referido</em>
                    </div>
                    {filtered.length > 0 ? (
                        filtered.map((opt) => (
                            <div
                                key={opt.id}
                                className={`creatable-option ${value && value.id === opt.id ? 'selected' : ''}`}
                                onMouseDown={() => handleSelect(opt)}
                            >
                                <strong>{opt.nombre_completo}</strong>
                                {opt.alias && (
                                    <span style={{ marginLeft: 6, opacity: 0.75 }}>“{opt.alias}”</span>
                                )}
                                {opt.numero_cedula && (
                                    <span style={{ marginLeft: 8, opacity: 0.6, fontSize: '0.85em' }}>
                                        C.C. {opt.numero_cedula}
                                    </span>
                                )}
                            </div>
                        ))
                    ) : (
                        <div className="creatable-option creatable-option--empty">Sin resultados</div>
                    )}
                </div>
            )}
        </div>
    );
};

// ── Componente Principal ─────────────────────────────────────────────────────

export default function FormUser() {
    const [formData, setFormData] = useState<FormDataType>({
        nombre_completo: '',
        alias: '',
        telefono: '',
        genero: '',
        lugarVotacion: '',
        mesaVotacion: '',
        municipio_id: null,
        direccion: '',
        cedula: '',
        referido_id: null,
    });

    const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
    const [municipios, setMunicipios] = useState<Municipio[]>([]);
    const [loadingMunicipios, setLoadingMunicipios] = useState(false);
    const [selectedDeptId, setSelectedDeptId] = useState<number | null>(null);

    const [presentadorOptions, setPresentadorOptions] = useState<PresentadorOption[]>([]);
    const [loadingPresentador, setLoadingPresentador] = useState(true);
    const [selectedPresentador, setSelectedPresentador] = useState<PresentadorOption | null>(null);

    const [submitting, setSubmitting] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');

    // "Más datos (opcional)": el registro inicial solo necesita el nombre
    const [masDatos, setMasDatos] = useState(false);

    // Posibles duplicados por nombre y/o alias (solo avisan, no bloquean)
    const [coincidencias, setCoincidencias] = useState<Coincidencia[]>([]);
    const [buscandoCoincidencias, setBuscandoCoincidencias] = useState(false);

    useEffect(() => {
        const nombre = formData.nombre_completo.trim();
        const alias = formData.alias.trim();
        if (nombre.length < NOMBRE_MIN && alias.length < ALIAS_MIN) {
            setCoincidencias([]);
            setBuscandoCoincidencias(false);
            return;
        }

        // Espera a que el usuario deje de escribir antes de consultar
        const controller = new AbortController();
        const timer = setTimeout(async () => {
            setBuscandoCoincidencias(true);
            try {
                const params = new URLSearchParams();
                if (nombre.length >= NOMBRE_MIN) params.set('nombre', nombre);
                if (alias.length >= ALIAS_MIN) params.set('alias', alias);
                const res = await fetch(
                    `${API_BASE_URL}/referidos/coincidencias?${params.toString()}`,
                    { headers: authHeaders(), signal: controller.signal }
                );
                const result = await res.json();
                setCoincidencias(result.status === 'success' ? result.data : []);
            } catch (err: any) {
                if (err?.name === 'AbortError') return;
                setCoincidencias([]);
            } finally {
                if (!controller.signal.aborted) setBuscandoCoincidencias(false);
            }
        }, COINCIDENCIAS_DEBOUNCE_MS);

        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [formData.nombre_completo, formData.alias]);

    // ── Carga inicial ──

    useEffect(() => {
        fetchDepartamentos();
        fetchPresentadorOptions();
    }, []);

    const fetchDepartamentos = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/departamentos`);
            const result = await res.json();
            if (result.status === 'success') {
                setDepartamentos(result.data);
            }
        } catch (err) {
            console.error('Error cargando departamentos:', err);
        }
    };

    const fetchMunicipios = async (deptId: number) => {
        setLoadingMunicipios(true);
        try {
            const res = await fetch(`${API_BASE_URL}/municipios/${deptId}`);
            const result = await res.json();
            if (result.status === 'success') {
                setMunicipios(result.data);
            }
        } catch (err) {
            console.error('Error cargando municipios:', err);
        } finally {
            setLoadingMunicipios(false);
        }
    };

    const fetchPresentadorOptions = async () => {
        setLoadingPresentador(true);
        try {
            const res = await fetch(`${API_BASE_URL}/referidos`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            const result = await res.json();
            if (result.status === 'success') {
                setPresentadorOptions(result.data);
            }
        } catch (err) {
            console.error('Error cargando presentadores:', err);
        } finally {
            setLoadingPresentador(false);
        }
    };

    // ── Handlers ──

    const handleInputChange = (fieldName: string, value: any) => {
        setFormData(prev => ({ ...prev, [fieldName]: value }));
    };

    const handleDepartamentoChange = (deptId: number | null) => {
        setSelectedDeptId(deptId);
        setMunicipios([]);
        handleInputChange('municipio_id', null);
        if (deptId) {
            fetchMunicipios(deptId);
        }
    };

    const handlePresentadorChange = (opt: PresentadorOption | null) => {
        setSelectedPresentador(opt);
        handleInputChange('referido_id', opt ? opt.id : null);
    };

    const emptyForm = (): FormDataType => ({
        nombre_completo: '', alias: '', telefono: '', genero: '',
        lugarVotacion: '', mesaVotacion: '',
        municipio_id: null, direccion: '', cedula: '',
        referido_id: null,
    });

    const handleSubmit = async () => {
        if (!formData.nombre_completo.trim()) {
            setAlertMessage("El nombre completo es obligatorio");
            setShowAlert(true);
            return;
        }
        setSubmitting(true);
        try {
            await handleRegister(formData);
            setAlertMessage("Registro exitoso");
            setShowAlert(true);
            setFormData(emptyForm());
            setSelectedPresentador(null);
            setSelectedDeptId(null);
            setMunicipios([]);
            setMasDatos(false);
            // Refrescar opciones de presentador con el nuevo referido
            await fetchPresentadorOptions();
        } catch (err: any) {
            console.error(err);
            setAlertMessage(err?.message || "Error al registrar referido");
            setShowAlert(true);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <IonPage className="app-page">
            <AppBackground />

            <PageHeader title="Registrar Referido" subtitle="Solo el nombre es obligatorio" backHref="/panel" />

            <IonContent className="content-transparent ion-padding-horizontal">
                <div className="app-container">
                            <IonCard className="app-card form-card">
                                <IonCardContent>
                                    <IonGrid>
                                        <IonRow>

                                            {/* Nombre: único dato obligatorio del registro inicial */}
                                            <IonCol size="12" sizeMd="7">
                                                <IonItem className="custom-input-item" lines="none">
                                                    <IonInput
                                                        label="Nombre Completo *"
                                                        labelPlacement="stacked"
                                                        placeholder="Ingrese el nombre completo"
                                                        value={formData.nombre_completo}
                                                        onIonInput={(e: any) => handleInputChange('nombre_completo', e.detail.value ?? '')}
                                                    />
                                                    {buscandoCoincidencias && <IonSpinner slot="end" name="crescent" className="select-spinner" />}
                                                </IonItem>
                                            </IonCol>

                                            {/* Alias / apodo */}
                                            <IonCol size="12" sizeMd="5">
                                                <IonItem className="custom-input-item" lines="none">
                                                    <IonInput
                                                        label="Alias / apodo"
                                                        labelPlacement="stacked"
                                                        placeholder="Cómo le dicen"
                                                        value={formData.alias}
                                                        onIonInput={(e: any) => handleInputChange('alias', e.detail.value ?? '')}
                                                    />
                                                </IonItem>
                                            </IonCol>

                                            {/* Posibles coincidencias: solo avisan, se puede registrar igual */}
                                            {coincidencias.length > 0 && (
                                                <IonCol size="12">
                                                    <div className="match-box">
                                                        <p className="match-box-title">
                                                            <IonIcon icon={alertCircleOutline} />
                                                            Posibles coincidencias ({coincidencias.length})
                                                        </p>
                                                        <p className="match-box-hint">
                                                            Revisa que no sea la misma persona. Si es otra, puedes registrarla igual.
                                                        </p>
                                                        <ul className="match-list">
                                                            {coincidencias.map(c => (
                                                                <li key={c.id} className="match-item">
                                                                    <div className="match-info">
                                                                        <strong>{c.nombre_completo}</strong>
                                                                        {c.alias && <span className="match-alias">“{c.alias}”</span>}
                                                                        <span className="match-meta">
                                                                            {[
                                                                                c.numero_cedula && `C.C. ${c.numero_cedula}`,
                                                                                c.municipio_nombre,
                                                                                c.registrado_por && `registrado por ${c.registrado_por}`,
                                                                            ].filter(Boolean).join(' · ')}
                                                                        </span>
                                                                    </div>
                                                                    <Link className="app-link" to={`/editar-referido/${c.id}`}>Ver</Link>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </IonCol>
                                            )}

                                            {/* Resto de datos: opcionales, se pueden completar después al editar */}
                                            <IonCol size="12">
                                                <button
                                                    type="button"
                                                    className={`more-data-toggle ${masDatos ? 'open' : ''}`}
                                                    onClick={() => setMasDatos(v => !v)}
                                                    aria-expanded={masDatos}
                                                >
                                                    <span>Más datos <em>(opcional)</em></span>
                                                    <IonIcon icon={chevronDownOutline} />
                                                </button>
                                            </IonCol>

                                            {masDatos && (<>
                                            {/* Cédula */}
                                            <IonCol size="12" sizeMd="6">
                                                <IonItem className="custom-input-item" lines="none">
                                                    <IonInput
                                                        label="Cédula"
                                                        labelPlacement="stacked"
                                                        inputmode="numeric"
                                                        placeholder="Número de identificación"
                                                        value={formData.cedula}
                                                        onIonInput={(e: any) => handleInputChange('cedula', e.detail.value)}
                                                    />
                                                </IonItem>
                                            </IonCol>

                                            {/* Teléfono */}
                                            <IonCol size="12" sizeMd="6">
                                                <IonItem className="custom-input-item" lines="none">
                                                    <IonInput
                                                        label="Teléfono"
                                                        labelPlacement="stacked"
                                                        type="tel"
                                                        placeholder="Número de contacto"
                                                        value={formData.telefono}
                                                        onIonInput={(e: any) => handleInputChange('telefono', e.detail.value)}
                                                    />
                                                </IonItem>
                                            </IonCol>

                                            {/* Género */}
                                            <IonCol size="12" sizeMd="8">
                                                <div className="gender-selector-container">
                                                    <label className="gender-selector-label">Género</label>
                                                    <div className="gender-options">
                                                        <div
                                                            className={`gender-option-card ${formData.genero === 'Hombre' ? 'selected' : ''}`}
                                                            onClick={() => handleInputChange('genero', 'Hombre')}
                                                        >
                                                            <div className="gender-checkbox-circle">
                                                                <div className="gender-checkbox-inner" />
                                                            </div>
                                                            <span>Hombre</span>
                                                        </div>
                                                        <div
                                                            className={`gender-option-card ${formData.genero === 'Mujer' ? 'selected' : ''}`}
                                                            onClick={() => handleInputChange('genero', 'Mujer')}
                                                        >
                                                            <div className="gender-checkbox-circle">
                                                                <div className="gender-checkbox-inner" />
                                                            </div>
                                                            <span>Mujer</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </IonCol>

                                            {/* Dirección */}
                                            <IonCol size="12">
                                                <IonItem className="custom-input-item" lines="none">
                                                    <IonInput
                                                        label="Dirección de Residencia"
                                                        labelPlacement="stacked"
                                                        placeholder="Dirección completa"
                                                        value={formData.direccion}
                                                        onIonInput={(e: any) => handleInputChange('direccion', e.detail.value)}
                                                    />
                                                </IonItem>
                                            </IonCol>

                                            {/* Departamento */}
                                            <IonCol size="12" sizeMd="6">
                                                <IonItem className="custom-input-item" lines="none">
                                                    <IonSelect
                                                        label="Departamento"
                                                        labelPlacement="stacked"
                                                        placeholder="Seleccione departamento"
                                                        value={selectedDeptId}
                                                        onIonChange={(e: any) => handleDepartamentoChange(e.detail.value)}
                                                    >
                                                        {departamentos.map(d => (
                                                            <IonSelectOption key={d.id} value={d.id}>
                                                                {d.nombre}
                                                            </IonSelectOption>
                                                        ))}
                                                    </IonSelect>
                                                </IonItem>
                                            </IonCol>

                                            {/* Municipio */}
                                            <IonCol size="12" sizeMd="6">
                                                <IonItem className="custom-input-item" lines="none">
                                                    <IonSelect
                                                        label="Municipio"
                                                        labelPlacement="stacked"
                                                        placeholder={loadingMunicipios ? "Cargando..." : (selectedDeptId ? "Seleccione municipio" : "Primero seleccione departamento")}
                                                        value={formData.municipio_id}
                                                        disabled={!selectedDeptId || loadingMunicipios}
                                                        onIonChange={(e: any) => handleInputChange('municipio_id', e.detail.value)}
                                                    >
                                                        {municipios.map(m => (
                                                            <IonSelectOption key={m.id} value={m.id}>
                                                                {m.nombre}
                                                            </IonSelectOption>
                                                        ))}
                                                    </IonSelect>
                                                </IonItem>
                                            </IonCol>

                                            {/* Lugar de Votación */}
                                            <IonCol size="12" sizeMd="8">
                                                <IonItem className="custom-input-item" lines="none">
                                                    <IonInput
                                                        label="Lugar de Votación"
                                                        labelPlacement="stacked"
                                                        placeholder="Puesto de votación"
                                                        value={formData.lugarVotacion}
                                                        onIonInput={(e: any) => handleInputChange('lugarVotacion', e.detail.value)}
                                                    />
                                                </IonItem>
                                            </IonCol>

                                            {/* Mesa de Votación */}
                                            <IonCol size="12" sizeMd="4">
                                                <IonItem className="custom-input-item" lines="none">
                                                    <IonInput
                                                        label="Mesa"
                                                        labelPlacement="stacked"
                                                        placeholder="Número de mesa"
                                                        value={formData.mesaVotacion}
                                                        onIonInput={(e: any) => handleInputChange('mesaVotacion', e.detail.value)}
                                                    />
                                                </IonItem>
                                            </IonCol>

                                            {/* Selector de Presentador (quién lo invitó) */}
                                            <IonCol size="12">
                                                <div className="custom-input-item-wrapper">
                                                    <PresentadorSelect
                                                        value={selectedPresentador}
                                                        onChange={handlePresentadorChange}
                                                        options={presentadorOptions}
                                                        loading={loadingPresentador}
                                                    />
                                                </div>
                                            </IonCol>
                                            </>)}

                                            {/* Botón de envío */}
                                            <IonCol size="12" className="ion-margin-top">
                                                <IonButton
                                                    expand="block"
                                                    onClick={handleSubmit}
                                                    className="app-btn-primary"
                                                    shape="round"
                                                    disabled={submitting}
                                                >
                                                    {submitting
                                                        ? <IonSpinner name="crescent" />
                                                        : 'Registrar Referido'
                                                    }
                                                </IonButton>
                                            </IonCol>

                                        </IonRow>
                                    </IonGrid>
                                </IonCardContent>
                            </IonCard>
                </div>
            </IonContent>
            <AlertAceptar
                isOpen={showAlert}
                message={alertMessage}
                onDidDismiss={() => setShowAlert(false)}
            />
        </IonPage>
    );
}